import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface InviteCode {
  id: string
  code: string
  createdAt: string
  usedBy?: string
  usedAt?: string
  isActive: boolean
  maxUses: number
  currentUses: number
  expiresAt?: string
  description?: string
  creator: { username: string }
  user?: { username: string }
}

export default function InviteManager() {
  const { user } = useAuth()
  const [invites, setInvites] = useState<InviteCode[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  
  // Form state
  const [maxUses, setMaxUses] = useState(1)
  const [expiresInDays, setExpiresInDays] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (user?.role === 'admin') {
      loadInvites()
    }
  }, [user])

  const loadInvites = async () => {
    try {
      const response = await fetch('/api/invites', {
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        setInvites(data)
      }
    } catch (error) {
      console.error('Error loading invites:', error)
    }
  }

  const generateInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    try {
      const body: any = {
        maxUses,
        description: description.trim() || undefined
      }
      
      if (expiresInDays) {
        body.expiresInDays = parseInt(expiresInDays)
      }

      const response = await fetch('/api/invites/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (response.ok) {
        setMessage(`Invite code generated: ${data.code}`)
        setIsSuccess(true)
        setDescription('')
        setExpiresInDays('')
        setMaxUses(1)
        loadInvites() // Refresh the list
      } else {
        setMessage(data.message || 'Failed to generate invite code')
        setIsSuccess(false)
      }
    } catch (error) {
      setMessage('Network error occurred')
      setIsSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  const deactivateInvite = async (inviteId: string) => {
    try {
      const response = await fetch(`/api/invites/${inviteId}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (response.ok) {
        setMessage('Invite code deactivated')
        setIsSuccess(true)
        loadInvites()
      } else {
        setMessage('Failed to deactivate invite code')
        setIsSuccess(false)
      }
    } catch (error) {
      setMessage('Network error occurred')
      setIsSuccess(false)
    }
  }

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code)
    setMessage(`Copied ${code} to clipboard`)
    setIsSuccess(true)
  }

  if (user?.role !== 'admin') {
    return (
      <div className="invite-manager">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You must be an admin to access invite code management.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="invite-manager">
      <div className="invite-header">
        <h1>Invite Code Manager</h1>
        <p>Generate and manage invite codes for beta access</p>
      </div>

      <div className="invite-content">
        <div className="generate-section">
          <h2>Generate New Invite Code</h2>
          <form onSubmit={generateInvite} className="generate-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="maxUses">Max Uses</label>
                <input
                  type="number"
                  id="maxUses"
                  min="1"
                  max="100"
                  value={maxUses}
                  onChange={(e) => setMaxUses(parseInt(e.target.value) || 1)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="expiresInDays">Expires In (Days)</label>
                <input
                  type="number"
                  id="expiresInDays"
                  min="1"
                  placeholder="Never expires"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <input
                type="text"
                id="description"
                placeholder="e.g., Beta tester access, VIP user, etc."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <button type="submit" disabled={loading} className="generate-btn">
              {loading ? 'Generating...' : 'Generate Invite Code'}
            </button>
          </form>

          {message && (
            <div className={`message ${isSuccess ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </div>

        <div className="invites-list">
          <h2>All Invite Codes</h2>
          <div className="invites-table">
            {invites.length === 0 ? (
              <p>No invite codes found.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Uses</th>
                    <th>Description</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((invite) => (
                    <tr key={invite.id}>
                      <td>
                        <code className="invite-code" onClick={() => copyToClipboard(invite.code)}>
                          {invite.code}
                        </code>
                      </td>
                      <td>
                        <span className={`status ${invite.isActive ? 'active' : 'inactive'}`}>
                          {invite.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{invite.currentUses}/{invite.maxUses}</td>
                      <td>{invite.description || '—'}</td>
                      <td>{new Date(invite.createdAt).toLocaleDateString()}</td>
                      <td>
                        {invite.expiresAt 
                          ? new Date(invite.expiresAt).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                      <td>
                        {invite.isActive && (
                          <button 
                            onClick={() => deactivateInvite(invite.id)}
                            className="deactivate-btn"
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .invite-manager {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          background: #1a202c;
          color: #e2e8f0;
          min-height: 100vh;
        }

        .invite-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .invite-header h1 {
          color: #4cc380;
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
        }

        .invite-header p {
          color: #a0aec0;
          font-size: 1.1rem;
        }

        .invite-content {
          display: grid;
          gap: 3rem;
        }

        .generate-section {
          background: #2d3748;
          padding: 2rem;
          border-radius: 12px;
          border: 1px solid #4a5568;
        }

        .generate-section h2 {
          color: #4cc380;
          margin-bottom: 1.5rem;
        }

        .generate-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          color: #e2e8f0;
          font-weight: 600;
        }

        .form-group input {
          padding: 12px 16px;
          border: 2px solid #4a5568;
          border-radius: 8px;
          background: #1a202c;
          color: #e2e8f0;
          font-size: 1rem;
        }

        .form-group input:focus {
          outline: none;
          border-color: #4cc380;
          box-shadow: 0 0 0 3px rgba(76, 195, 128, 0.1);
        }

        .generate-btn {
          background: #4cc380;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.3s;
        }

        .generate-btn:hover:not(:disabled) {
          background: #2d8f5f;
        }

        .generate-btn:disabled {
          background: #718096;
          cursor: not-allowed;
        }

        .message {
          padding: 12px 16px;
          border-radius: 8px;
          margin-top: 1rem;
          text-align: center;
          border: 1px solid;
        }

        .message.success {
          background: rgba(76, 195, 128, 0.1);
          color: #4cc380;
          border-color: #4cc380;
        }

        .message.error {
          background: rgba(231, 76, 60, 0.1);
          color: #e74c3c;
          border-color: #e74c3c;
        }

        .invites-list {
          background: #2d3748;
          padding: 2rem;
          border-radius: 12px;
          border: 1px solid #4a5568;
        }

        .invites-list h2 {
          color: #4cc380;
          margin-bottom: 1.5rem;
        }

        .invites-table {
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th, td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #4a5568;
        }

        th {
          background: #1a202c;
          color: #4cc380;
          font-weight: 600;
        }

        .invite-code {
          background: #1a202c;
          padding: 4px 8px;
          border-radius: 4px;
          font-family: monospace;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .invite-code:hover {
          background: #4a5568;
        }

        .status {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .status.active {
          background: rgba(76, 195, 128, 0.2);
          color: #4cc380;
        }

        .status.inactive {
          background: rgba(231, 76, 60, 0.2);
          color: #e74c3c;
        }

        .deactivate-btn {
          background: #e74c3c;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 0.9rem;
          cursor: pointer;
          transition: background-color 0.3s;
        }

        .deactivate-btn:hover {
          background: #c0392b;
        }

        .access-denied {
          text-align: center;
          padding: 3rem;
          background: #2d3748;
          border-radius: 12px;
          border: 1px solid #4a5568;
        }

        .access-denied h2 {
          color: #e74c3c;
          margin-bottom: 1rem;
        }

        @media (max-width: 768px) {
          .invite-manager {
            padding: 1rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .invites-table {
            font-size: 0.9rem;
          }

          th, td {
            padding: 8px;
          }
        }
      `}</style>
    </div>
  )
}